import { useEffect, useRef } from "react";
import type { AssistantMode, ToolEvent, Turn } from "../types";
import { Message } from "./Message";
import { ToolEventRow } from "./ToolEventRow";
import { EmptyState } from "./EmptyState";
import { SkeletonMessages } from "./Skeleton";

interface Props {
  turns: Turn[];
  tools: ToolEvent[];
  isStreaming: boolean;
  historyLoading: boolean;
  onRetry: () => void;
  onRegenerate: () => void;
  onLike: (idx: number) => void;
  onDislike: (idx: number) => void;
  onSwitchVersion: (idx: number, versionIdx: number) => void;
  onPickExample: (text: string) => void;
  onDelete?: (idx: number) => void;
  userName?: string;
  mode: AssistantMode;
}

export function MessageList({
  turns,
  tools,
  isStreaming,
  historyLoading,
  onRetry,
  onRegenerate,
  onLike,
  onDislike,
  onSwitchVersion,
  onPickExample,
  onDelete,
  userName,
  mode,
}: Props) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [turns, tools]);

  // troca de conversa: mostra skeleton enquanto carrega o histórico
  if (historyLoading && turns.length === 0) {
    return (
      <main className="messages" ref={ref}>
        <div className="messages-inner">
          <SkeletonMessages blocks={3} />
        </div>
      </main>
    );
  }

  if (turns.length === 0) {
    return (
      <main className="messages" ref={ref}>
        <EmptyState onPick={onPickExample} userName={userName} mode={mode} />
      </main>
    );
  }

  const lastIdx = turns.length - 1;
  const lastTool: ToolEvent | null = tools.length > 0 ? (tools[tools.length - 1] ?? null) : null;

  return (
    <main className="messages" ref={ref} aria-live="polite">
      <div className="messages-inner">
        {turns.map((turn, idx) => {
          const isLast = idx === lastIdx;
          const isLastAssistant = isLast && turn.role === "assistant";
          if (turn.deletedAt) {
            // mensagem apagada (soft delete): tombstone no lugar do conteúdo
            return (
              <div key={`${idx}-${turn.ts}`} className={`msg msg-${turn.role} msg-deleted`}>
                <em className="msg-deleted-note">mensagem apagada</em>
              </div>
            );
          }
          return (
            <div key={`${idx}-${turn.ts}`}>
              <Message
                turn={turn}
                turnIdx={idx}
                isThinking={isLastAssistant && isStreaming}
                currentStep={isLastAssistant && isStreaming ? lastTool : null}
                isLastAssistant={isLastAssistant && !isStreaming}
                onRetry={isLastAssistant && turn.errored ? onRetry : undefined}
                onRegenerate={onRegenerate}
                onLike={onLike}
                onDislike={onDislike}
                onSwitchVersion={onSwitchVersion}
                onDelete={onDelete}
              />
              {/* Ferramentas DESTA resposta (o backend só envia pra sysadmin).
                  Na mensagem em streaming, o acordeão vivo abaixo já cobre. */}
              {turn.role === "assistant" && (turn.tools?.length ?? 0) > 0 && !(isLastAssistant && isStreaming) && (
                <details className="tools-accordion per-turn">
                  <summary>
                    <svg className="caret" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M6 4l4 4-4 4" />
                    </svg>
                    Ferramentas desta resposta ({turn.tools!.length})
                  </summary>
                  <div className="tools-list">
                    {turn.tools!.map((t, i) => (
                      <ToolEventRow key={`${idx}-tool-${i}`} event={{ id: `${idx}-tool-${i}`, name: t.name, input: t.input }} />
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })}
        {isStreaming && tools.length > 0 && (
          <details className="tools-accordion">
            <summary>
              <svg className="caret" width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M6 4l4 4-4 4" />
              </svg>
              Ver ferramentas usadas ({tools.length})
            </summary>
            <div className="tools-list">
              {tools.map((t) => (
                <ToolEventRow key={t.id} event={t} />
              ))}
            </div>
          </details>
        )}
      </div>
    </main>
  );
}
